import type {
  CrmLeadIntegrationsMap,
  CrmLeadProviderChatMeta,
  CrmLeadVkInfo,
} from '@/api/types/crm/crmLeadFunnels.types'
import { CRM_LEAD_PROVIDER_CHAT_META_EMPTY } from '@/api/types/crm/crmLeadFunnels.types'

/** Достаёт VK-мету из get-lead: `integrations.vkontakte` или legacy `vk`. */
export const extractVkChatMetaFromPayload = (
  payload: unknown,
): CrmLeadProviderChatMeta | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined
  }

  const root = payload as {
    integrations?: CrmLeadIntegrationsMap
    vk?: CrmLeadVkInfo
    lead?: {
      integrations?: CrmLeadIntegrationsMap
      vk?: CrmLeadVkInfo
    }
  }

  const fromRootIntegrations = root.integrations?.vkontakte
  if (fromRootIntegrations) {
    return fromRootIntegrations
  }
  if (root.vk) {
    return root.vk
  }

  const nested = root.lead
  if (!nested || typeof nested !== 'object') {
    return undefined
  }

  if (nested.integrations?.vkontakte) {
    return nested.integrations.vkontakte
  }
  if (nested.vk) {
    return nested.vk
  }

  return undefined
}

/** Map интеграций из тела get-lead (корень или lead). */
export const extractIntegrationsMapFromPayload = (
  payload: unknown,
): CrmLeadIntegrationsMap | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined
  }

  const root = payload as {
    integrations?: CrmLeadIntegrationsMap
    lead?: { integrations?: CrmLeadIntegrationsMap; vk?: CrmLeadVkInfo }
    vk?: CrmLeadVkInfo
  }

  if (root.integrations && typeof root.integrations === 'object') {
    return root.integrations
  }

  if (root.lead?.integrations && typeof root.lead.integrations === 'object') {
    return root.lead.integrations
  }

  const vk = root.vk ?? root.lead?.vk
  if (vk) {
    return { vkontakte: vk }
  }

  return undefined
}

export const withLeadVkChatFromPayload = <T extends object>(
  lead: T,
  payload: unknown,
): T => {
  const integrations = extractIntegrationsMapFromPayload(payload)
  const vkMeta = extractVkChatMetaFromPayload(payload)

  if (!integrations && !vkMeta) {
    return lead
  }

  const resolvedVk =
    vkMeta ?? integrations?.vkontakte ?? CRM_LEAD_PROVIDER_CHAT_META_EMPTY

  return {
    ...lead,
    ...(integrations ? { integrations } : {}),
    vk: resolvedVk,
  }
}
